/**
 * Exporting a report.
 *
 * **The file contains exactly what is on the screen.** No hidden columns, no
 * joined-in extras, and above all nothing that could identify a student — a
 * spreadsheet leaves the building in a way a web page does not, and the moment
 * a uid appears in one, every promise the product makes about student data is
 * only as good as whoever has the file.
 *
 * The allow-list is the mechanism. A column not named here cannot be exported,
 * so adding a field to a report does not silently add it to the file.
 */

export type ExportColumn = { key: string; headerKey: string };

/**
 * Field names that may never appear in an export, whatever a caller asks for.
 *
 * Matched after stripping non-letters and lower-casing, so `student_id`,
 * `studentId` and `STUDENTID` are the same name. `teacherUid` is included:
 * teacher activity is exported by NAME because a person's own name in a report
 * their employer already sees is not a disclosure — but the opaque identifier
 * is of no use to a reader and every use to a re-identifier.
 */
const FORBIDDEN = new Set([
  "uid",
  "uids",
  "userid",
  "studentid",
  "studentids",
  "teacheruid",
  "authoruid",
  "participant",
  "participants",
  "roster",
  "register",
  "attendance",
  "displayname",
  "firstname",
  "lastname",
  "email",
  "username",
  "deviceid",
  "score",
  "scores",
  "note",
  "notes",
]);

function normalise(key: string): string {
  return key.toLowerCase().replace(/[^a-z]/g, "");
}

export function isForbiddenColumn(key: string): boolean {
  return FORBIDDEN.has(normalise(key));
}

/** Throws rather than filtering: a silently dropped column is a wrong export. */
export function assertExportable(columns: readonly ExportColumn[]): void {
  const offenders = columns.filter((column) => isForbiddenColumn(column.key));
  if (offenders.length > 0) {
    throw new Error(
      `[export] refusing to export identifying columns: ${offenders
        .map((column) => column.key)
        .join(", ")}`
    );
  }
}

/**
 * One CSV cell.
 *
 * Quoted whenever it contains a separator, a quote or a newline, and a leading
 * `=`, `+`, `-` or `@` is prefixed with an apostrophe — a spreadsheet treats
 * those as formulas, and a scenario titled `=cmd|...` is a well-known way to
 * turn an export into an attack on whoever opens it.
 */
export function csvCell(value: unknown): string {
  const text = value === null || value === undefined ? "" : String(value);
  const guarded = /^[=+\-@\t\r]/.test(text) ? `'${text}` : text;
  return /[",\n\r;]/.test(guarded) ? `"${guarded.replace(/"/g, '""')}"` : guarded;
}

export function toCsv(
  columns: readonly ExportColumn[],
  rows: readonly Record<string, unknown>[],
  translate: (key: string) => string
): string {
  assertExportable(columns);

  const header = columns.map((column) => csvCell(translate(column.headerKey))).join(",");
  const body = rows.map((row) =>
    columns.map((column) => csvCell(row[column.key])).join(",")
  );
  // A byte-order mark, so Excel opens a German export without mangling every
  // umlaut. Written as an escape rather than as the character itself: an
  // invisible byte in a source file is a thing nobody can review.
  return `\uFEFF${[header, ...body].join("\r\n")}\r\n`;
}

/**
 * What the file is called.
 *
 * Institution, report and range, because a folder of `report.csv` files is a
 * folder nobody can use six months later.
 */
export function exportFilename(
  institution: string,
  report: string,
  range: { from: string; to: string }
): string {
  const slug = (value: string) =>
    value
      .toLowerCase()
      .normalize("NFD")
      .replace(/[̀-ͯ]/g, "")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");

  const day = (value: string) => (Date.parse(value) ? value.slice(0, 10) : "");
  return `${slug(institution)}-${slug(report)}-${day(range.from)}-${day(range.to)}.csv`;
}

/** Hands the file to the browser without anything reaching a server. */
export function downloadCsv(filename: string, csv: string): void {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}
